import * as api from '@gitbook/api-client';
import { checkIsPaidSiteProductKey, checkIsUnrestrictedProductKey } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import { Button, DialogRoot, DialogTrigger } from '@gitbook/spine-react';

import { useCheckoutSite, useSiteCapabilityContext, useTrial } from '../../../state';
import { PublishDialog } from './Publishing/PublishDialog';
import { useTogglePublishedModal } from './Publishing/PublishSuccessDialog';

export function SitePublishButton(props: {
    site: api.Site;
    organization: api.Organization;
    onPress?: () => void;
}) {
    const { site, organization, onPress } = props;
    const { canAdminSite } = useSiteCapabilityContext();
    const checkout = useCheckoutSite(organization.id, site);
    const togglePublishedModal = useTogglePublishedModal();
    const trial = useTrial({ organization });
    const isTrialingOrg = trial.status === api.BillingTrialStatus.Active;
    const isUnrestrictedOrg = checkIsUnrestrictedProductKey(organization.plan);
    const isPaidSite = checkIsPaidSiteProductKey(site.type);

    const notPublishableReason: string | null = (() => {
        if (!canAdminSite) {
            return 'You need creator permissions or higher on a linked space to publish this site';
        }
        if (site.siteSpaces <= 0) {
            return 'Link a space to publish this site';
        }
        return null;
    })();

    async function checkoutAndPublish() {
        onPress?.();
        const published = await checkout.onPress();
        togglePublishedModal(published);
    }

    /*
     * Skip publish dialog if:
     *  - the organization is trialing, enterprise, community or internal
     *  - the site is not paid (legacy sites, basic or sponsored)
     */
    const skipPublishDialog = isTrialingOrg || isUnrestrictedOrg || !isPaidSite;

    const button = (
        <Button
            kind="primary"
            label="Publish"
            leadingIcon={Icon.Publish}
            disabled={Boolean(notPublishableReason)}
            title={notPublishableReason ?? undefined}
            onPress={skipPublishDialog ? checkoutAndPublish : onPress}
        />
    );

    if (skipPublishDialog) {
        return button;
    }

    return (
        <DialogRoot>
            <DialogTrigger>{button}</DialogTrigger>
            {/* Even if the site is published, we keep it in the DOM to have the close animation */}
            <PublishDialog />
        </DialogRoot>
    );
}
